<script lang="ts">
	import OptimizerPageOpenOffer from './OptimizerPageOpenedOffer.svelte'
	import { onMount, setContext } from 'svelte'
	import { OPTIMIZER_PAYS_KICKBACK } from '../types/enums'
	import type { Lot } from './lot'
	import OptimizerPageWithDeal from './OptimizerPageWithDeal.svelte'
	import OptimizerPageRejected from './OptimizerPageRejected.svelte'
	import PubPage404 from '../pages/PubPage404.svelte'
	import { addNotification, contactData, loadUserInfo } from '../stores'
	import { type RequestDto, RequestedEntityType, RequestType } from '../bid/types/requestDto'
	import { writable, type Writable } from 'svelte/store'
	import type { RequestCollecting } from '../bid/types/requestCollecting'
	import { EmptyOffer, type OfferDesignatedEntity, type Offers } from '../bid/types/offer'
	import type { ContactData } from '../types/contactData'
	import { Facts } from '../bid/types/facts'
	import { fetchUtils } from '../utils/fetchUtils'
	import {
		redirectToLoginOnLoggedOutHandled404,
		redirectToNotFoundRoute
	} from '../utils/errorRedirect'
	import { NotificationType } from '../types/notification'
	import type { Deal } from '../bid/types/deal'
	import type { DealActualValues } from '../types/dealActualValues'
	import type { DealPartnerConnect } from '../types/dealPartner'

	loadUserInfo()

	const baseUrl: string = '/api/optimizer'
	const baseUrlRequest: string = baseUrl + '/request'
	const baseUrlLot: string = baseUrl + '/lot'

	const request: Writable<RequestDto | null> = setContext('request', writable(null))
	const offers: Writable<Offers> = setContext(
		'offers',
		writable({
			material: EmptyOffer(RequestType.MATERIAL),
			materialWithTransport: EmptyOffer(RequestType.MATERIAL_WITH_TRANSPORT),
			transport: EmptyOffer(RequestType.TRANSPORT)
		})
	)
	const offerDesignatedEntity: Writable<OfferDesignatedEntity | null> = setContext(
		'offerDesignatedEntity',
		writable(null)
	)
	const deal: Writable<Deal | null> = setContext('deal', writable(null))
	const dealPartnerConnect: Writable<DealPartnerConnect | null> = setContext(
		'dealPartnerConnect',
		writable(null)
	)
	const showKickback: Writable<boolean> = setContext('showKickback', writable(false))
	const lot: Writable<Lot | null> = setContext('lot', writable(null))
	const hasOfferFromOtherUser: Writable<boolean> = setContext(
		'hasOfferFromOtherUser',
		writable(false)
	)
	// Set to undefined in order to determine when a real value is loaded and set to the context store.
	// Used e.g. in PublicPageHeader component.
	let isSaasInternalLot: Writable<boolean | undefined> = setContext(
		'isSaasInternalLot',
		writable(undefined)
	)

	export let requestPublicId: string | undefined = undefined
	export let offerUserPublicId: string | undefined = undefined
	export let requestedEntityId: string | undefined = undefined
	export let requestedEntityType: RequestedEntityType | undefined = undefined
	export let lotPublicId: string | undefined = undefined

	setContext('urlParams', {
		get lotPublicId() {
			return lotPublicId
		},
		get offerUserPublicId() {
			return offerUserPublicId
		},
		get requestPublicId() {
			return requestPublicId
		},
		get requestedEntityId() {
			return requestedEntityId
		},
		get requestedEntityType() {
			return requestedEntityType
		}
	}) // getters ensure current prop values are returned even in Svelte 5 mixed-mode interop

	let pageComponent:
		| typeof OptimizerPageOpenOffer
		| typeof OptimizerPageRejected
		| typeof OptimizerPageWithDeal
		| typeof PubPage404
	let optimizerContactData: ContactData

	function getPageComponent(data: RequestCollecting) {
		if (data?.request?.rejectedByOptimizer) {
			return OptimizerPageRejected
		}
		if (data.deal) {
			// there exists a deal, i.e. the request/offers must have been accepted
			return OptimizerPageWithDeal
		}
		return OptimizerPageOpenOffer // otherwise always return the opened quote page
	}

	onMount(async () => {
		await fetchRequestCollecting()
	})

	async function fetchRequestCollecting() {
		let url = ''
		if (lotPublicId) {
			url = `${baseUrlLot}/${lotPublicId}`
		} else if (requestPublicId && offerUserPublicId) {
			if (offerUserPublicId.length > 0) {
				url = `${baseUrlRequest}/${requestPublicId}/${offerUserPublicId}`
			} else {
				url = `${baseUrlRequest}/${requestPublicId}`
			}
		}
		if (url.length > 0) {
			await fetchUtils
				.get(url)
				.then((data: RequestCollecting) => {
					$lot = { ...data.lot }
					$contactData = { ...data.contactData }
					$showKickback = $lot?.type ? OPTIMIZER_PAYS_KICKBACK.includes($lot.type) : false
					optimizerContactData = { ...data.optimizerContactData }
					$request = { ...data.request, facts: new Facts(data.request.facts) }
					if (data?.offerMaterialWithTransport) {
						// only overwrite if not null
						offers.update((o) => ({
							...o,
							materialWithTransport: {
								...data?.offerMaterialWithTransport,
								facts: new Facts(data?.offerMaterialWithTransport.facts)
							}
						}))

						$offerDesignatedEntity = {
							id: $offers.materialWithTransport?.offerCandidateRequestedEntityId,
							type: $offers.materialWithTransport?.offerCandidateRequestedEntityType
						}
					}
					if (data?.offerMaterial) {
						offers.update((o) => ({
							...o,
							material: {
								...data?.offerMaterial,
								facts: new Facts(data?.offerMaterial.facts)
							}
						}))

						// here we potentially overwrite it, but it is no problem, since we prefer the value of offerMaterial
						$offerDesignatedEntity = {
							id: $offers.material?.offerCandidateRequestedEntityId,
							type: $offers.material?.offerCandidateRequestedEntityType
						}
					}
					if (data.offerTransport) {
						offers.update((o) => ({
							...o,
							transport: {
								...data?.offerTransport,
								facts: new Facts(data?.offerTransport.facts)
							}
						}))
						// here we potentially overwrite it, but it is no problem, since we prefer the value of offerTransport
						$offerDesignatedEntity = {
							id: $offers.material?.offerCandidateRequestedEntityId,
							type: $offers.material?.offerCandidateRequestedEntityType
						}
					}
					if (data.deal) {
						let offererFacts: Facts = new Facts(data.deal?.offerActualValues?.totalFacts)
						let requestFacts: Facts = new Facts(data.deal?.requestActualValues?.totalFacts)
						let offerActualValues: DealActualValues = {
							...data.deal.offerActualValues,
							totalFacts: offererFacts
						}
						let requestActualValues: DealActualValues = {
							...data.deal.requestActualValues,
							totalFacts: requestFacts
						}
						$deal = {
							...data.deal,
							dealFacts: new Facts(data.deal?.dealFacts),
							offerActualValues,
							requestActualValues
						}
						$dealPartnerConnect = { ...data.connectData }
					}
					pageComponent = getPageComponent(data)
					$isSaasInternalLot = $lot.isSaasInternalLot
					$hasOfferFromOtherUser = data.hasOfferFromOtherUser ?? false
				})
				.catch(async (error) => {
					console.log(error)
					let notification = {
						message: error.message,
						type: NotificationType.ERROR,
						timeout: 0,
						dismissible: true
					}
					addNotification(notification)
					if (await redirectToLoginOnLoggedOutHandled404(error)) return
					// single-owner rule: doHandle owns 401/403/410 (handled:true); acting
					// again here would put /404 in the URL underneath the 40x page
					if (!error?.handled) {
						redirectToNotFoundRoute()
					}
				})
		}
	}

	$: ($lot,
		$lot?.type,
		($showKickback = $lot?.type ? OPTIMIZER_PAYS_KICKBACK.includes($lot.type) : false))
</script>

<svelte:component
	this={pageComponent}
	{optimizerContactData}
	on:offerChanged={fetchRequestCollecting}
/>
